import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, Sparkles, RefreshCw, MessageSquare, GraduationCap, Check } from 'lucide-react';
import { Button } from '../ui/button';

const STORAGE_KEY_PREFIX = 'tesc-onboarding-completed-';

const STEPS = [
  {
    icon: Sparkles,
    color: 'from-amber-400 to-orange-500',
    title: 'Bienvenue dans votre espace !',
    body: (firstName) => (
      <>
        <p>
          Bonjour <strong>{firstName}</strong>, ravi de vous compter parmi les familles de The English Speaking Center !
        </p>
        <p>
          Cet espace vous permet de suivre les inscriptions de vos enfants, recevoir les communications de l'école et préparer la rentrée prochaine.
        </p>
      </>
    ),
  },
  {
    icon: RefreshCw,
    color: 'from-blue-500 to-indigo-600',
    title: 'Réinscription en 1 clic',
    body: () => (
      <>
        <p>
          Quand les inscriptions seront ouvertes pour l'année suivante, vous verrez un bandeau bleu en haut du site et une carte
          <strong className="text-[#285BBB]"> « Bonjour [prénom] ! »</strong> sur la page d'inscription.
        </p>
        <p>
          Cliquez simplement sur le nom de votre enfant déjà inscrit, le formulaire sera <strong>pré-rempli automatiquement</strong>.
        </p>
      </>
    ),
  },
  {
    icon: MessageSquare,
    color: 'from-emerald-500 to-teal-600',
    title: 'Une question ? Contactez-nous',
    body: () => (
      <>
        <p>
          Pour toute question sur les cours, certifications ou inscriptions, vous pouvez nous joindre :
        </p>
        <ul className="space-y-1.5 text-sm">
          <li>📧 <strong>contact@theenglishspeakingcenter.fr</strong></li>
          <li>📞 <strong>06 26 71 60 22</strong></li>
          <li>📍 <strong>9 Allée Jean Jaurès, 13800 Istres</strong></li>
        </ul>
        <p className="text-sm text-[#6777A1]">
          Vous pouvez aussi utiliser la messagerie interne accessible depuis votre espace élève.
        </p>
      </>
    ),
  },
];

const ParentOnboarding = () => {
  const [user, setUser] = useState(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      if (!u) return;
      // Show only for parent role
      if (u.role !== 'parent') return;
      const key = STORAGE_KEY_PREFIX + u.id;
      if (localStorage.getItem(key) === '1') return;
      setUser(u);
      // Slight delay so the dashboard renders first
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    } catch {
      // noop
    }
  }, []);

  const close = (markCompleted = true) => {
    setOpen(false);
    if (markCompleted && user) {
      localStorage.setItem(STORAGE_KEY_PREFIX + user.id, '1');
    }
  };

  if (!open || !user) return null;

  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
        data-testid="parent-onboarding-modal"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: 'spring', stiffness: 280, damping: 28 }}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden relative"
        >
          {/* Close button */}
          <button
            onClick={() => close(true)}
            className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/80 hover:bg-slate-100 flex items-center justify-center text-slate-500"
            aria-label="Fermer"
            data-testid="onboarding-close"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Header gradient with icon */}
          <div className={`bg-gradient-to-br ${current.color} p-8 pb-12 relative`}>
            <div className="absolute -right-4 -top-4 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
            <div className="relative w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg">
              <Icon className="h-8 w-8 text-white" />
            </div>
          </div>

          {/* Content */}
          <div className="p-8 -mt-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.25 }}
                className="space-y-3"
              >
                <h2 className="text-2xl font-bold text-[#2C3C65]">{current.title}</h2>
                <div className="space-y-3 text-[#475569] text-sm leading-relaxed">
                  {current.body(user.prenom)}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Progress dots */}
            <div className="flex items-center justify-center gap-2 mt-8">
              {STEPS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setStep(i)}
                  className={`h-2 rounded-full transition-all ${
                    i === step ? 'w-8 bg-[#285BBB]' : i < step ? 'w-2 bg-[#285BBB]/60' : 'w-2 bg-slate-200'
                  }`}
                  aria-label={`Étape ${i + 1}`}
                  data-testid={`onboarding-dot-${i}`}
                />
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between gap-3 mt-6">
              {step > 0 ? (
                <Button
                  variant="outline"
                  onClick={() => setStep(step - 1)}
                  data-testid="onboarding-prev"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Précédent
                </Button>
              ) : (
                <button
                  onClick={() => close(true)}
                  className="text-sm text-[#6777A1] hover:text-[#285BBB] underline-offset-4 hover:underline"
                  data-testid="onboarding-skip"
                >
                  Passer
                </button>
              )}
              {!isLast ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  className="bg-[#285BBB] hover:bg-[#1e47a0] text-white"
                  data-testid="onboarding-next"
                >
                  Suivant
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={() => close(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="onboarding-finish"
                >
                  <Check className="h-4 w-4 mr-1" />
                  C'est parti !
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ParentOnboarding;
